/*
 * Resource area (audit Phase 3.3). Replaces the four legacy
 * wwwroot/js/Resource/*.js files with one module on the Phase 3.1 core.
 * No jQuery.
 *
 * The screen was redefined this session: /Resource is GLOBAL RESOURCES --
 * every resource with no parent application, i.e. true global rows (no domain)
 * and domain-level rows. Resources owned by an application are the other half
 * of the set and are administered from Application Detail > Resources
 * (Application.Resource in areas/application.ts). Before this the standalone
 * screens filtered by APPLICATION, duplicating that tab, and nothing rendered
 * them at all -- which is why a resource's PolicyId and its certificate
 * bindings had no reachable UI.
 *
 * What went rather than moved:
 *   Resource.GetApplicationUrl / GetResourceUrl -- an ad-hoc six-branch URI
 *     rule that read #ResourceAppId, an id no rendered page has ever had, so
 *     the "Resource URI" preview was wrong on every screen that showed it.
 *     Mcp.Core.Utility.McpResourceUri is the ONE composer; Scope.Preview below
 *     mirrors only its two parentless cases and takes the domain URL from the
 *     markup the server rendered.
 *   Resource.GetAvailableApplications -- the parent-application cascade, which
 *     this screen no longer has a select for.
 *   Resource.List.SetRowClickHandler -- a $(...).click() bind that ran only
 *     after a filter change, so rows were inert on first paint. Rows carry
 *     data-action now.
 *   Resource.List.SelectionFormFailure -- no caller.
 */
import { ajaxJson, ajaxModal, ajaxSwap } from '../core/ajax';
import { all, one } from '../core/dom';
import { serialize } from '../core/forms';
import { confirm, hideModal } from '../core/modal';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';

function value(id: string): string {
    return (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null)?.value ?? '';
}

function setHidden(selector: string, hidden: boolean): void {
    for (const node of all(selector)) node.classList.toggle('d-none', hidden);
}

/** The scope the list is showing: '' = every scope, '0' = global, n = a domain. */
function scope(): string {
    return value('ResourceScopeSelect');
}

function reloadList(): Promise<void> {
    const domainId = scope();
    return ajaxSwap({
        container: 'ResourceListContainer',
        url: `Resource/List${domainId === '' ? '' : `?domainId=${encodeURIComponent(domainId)}`}`,
        spinnerInto: true,
        alertInto: 'ResourceIndexAlertContainer',
        alertTitle: 'An error occurred attempting to update the list of resources',
    });
}

/**
 * McpResourceTypes.Domain. A Type='D' row's prefix IS its URL and it
 * self-references its own domain, so composing "prefix + owning domain" the
 * way every other row does renders famillium.com as
 * famillium.com.famillium.com -- a string nothing stores or matches. The
 * server-side composer guards this case first; the preview has to as well,
 * and the browser pass caught it doing exactly that on the Famillium row.
 */
const DOMAIN_TYPE = 'D';

/**
 * The Resource URI preview shared by the Create and Edit forms. Mirrors
 * McpResourceUri for the cases this screen can produce and nothing else: a
 * Domain row is its bare prefix, a parentless row with a domain is
 * `{prefix}.{domain}`, and one without a domain is the bare prefix. The domain
 * URL comes off the selected option's data-url, which the server rendered from
 * the same DomainModel the composer reads, so the two cannot drift.
 */
export const Scope = {
    Preview(): void {
        const target = one('ResourceUrl');
        if (!target) return;
        const select = document.getElementById('ResourceDomainId') as HTMLSelectElement | null;
        const domainUrl = select?.selectedOptions[0]?.getAttribute('data-url')?.trim() ?? '';
        const prefix = value('ResourceDomainPrefix').trim();
        const isDomain = value('ResourceType') === DOMAIN_TYPE;
        target.textContent = isDomain ? prefix
            : prefix && domainUrl ? `${prefix}.${domainUrl}`
            : prefix ? prefix
            : domainUrl;
    },

    /**
     * Changing scope changes the composed URI, and the URI is the identity every
     * stored grant is keyed by -- moving an EXISTING resource between domains
     * orphans its permissions, so that case confirms first and puts the select
     * back on cancel.
     *
     * A resource being created has no grants to orphan and no URI anyone refers
     * to yet, so the new-resource form applies the choice straight away. Asking
     * there would be a warning about consequences that cannot exist.
     */
    DomainChanged(this: HTMLSelectElement): void {
        const select = this;
        const chosen = select.value;
        if (chosen === previousScope) {
            Scope.Preview();
            return;
        }
        if (!select.closest('#EditResourceForm')) {
            previousScope = chosen;
            Scope.Preview();
            return;
        }
        void confirm({
            title: 'Change scope',
            message: 'A resource’s scope is part of its URI, which is how every permission '
                + 'refers to it. Existing grants will not follow it. Change the scope?',
            confirmText: 'Change',
            variant: 'warning',
            onConfirm: () => {
                previousScope = chosen;
                Scope.Preview();
            },
            onCancel: () => {
                select.value = previousScope;
                Scope.Preview();
            },
        });
    },
};

/**
 * The scope the form was last known to agree with, so a cancelled change can
 * be put back. Seeded by initResourceArea whenever a form lands in the page;
 * the legacy version kept one per mode on the handler object and never reset
 * it between modals.
 */
let previousScope = '';

export const List = {
    ScopeChanged(): void {
        void reloadList();
    },

    /** Row click -> the Edit modal. Rows were inert on first paint before. */
    RowClick(resourceId: number | string): void {
        setHidden(`#ResourceBusy${resourceId}`, false);
        void ajaxModal({
            modal: 'ResourceModal',
            url: `Resource/Edit/${resourceId}`,
            formId: 'EditResourceForm',
            focusId: 'Name',
            errorTitle: `Resource ${resourceId}`,
            errorMessage: 'Resource Edit failed to execute:',
            onComplete: () => setHidden(`#ResourceBusy${resourceId}`, true),
        });
    },

    DisplayCreateModal(): void {
        const domainId = scope();
        void ajaxModal({
            modal: 'ResourceModal',
            // The scope filter seeds the new resource's domain, but only when it
            // names one: '' is "every scope", which is not a domain a resource
            // can be created in.
            url: `Resource/Create${domainId === '' ? '' : `?domainId=${encodeURIComponent(domainId)}`}`,
            formId: 'CreateResourceForm',
            focusId: 'Name',
            errorTitle: 'New Resource',
            errorMessage: 'New Resource View failed to execute:',
        });
    },

    DeleteClick(resourceId: number | string, resourceName: string): void {
        void confirm({
            title: 'Delete Resource',
            message: `Delete resource "${resourceName}"?`,
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Resource/${resourceId}`,
                    method: 'DELETE',
                    alertInto: 'ResourceIndexAlertContainer',
                    errorTitle: 'Delete action failed to execute',
                    onSuccess: () => {
                        toast('success', `Resource "${resourceName}" has been deleted`);
                        void reloadList();
                    },
                });
            },
        });
    },
};

export const Create = {
    /**
     * POST the create form. On success the controller answers with the Edit
     * partial for the row it just wrote, so the operator lands on the screen
     * that can bind a policy and a certificate; on failure it answers with
     * this same form carrying ModelState.
     */
    SaveClick(): false {
        const form = one('CreateResourceForm') as HTMLFormElement | null;
        if (!form || !isValid(form)) return false;
        void ajaxSwap({
            container: 'ResourceCreateContainer',
            url: 'Resource/Create',
            method: 'POST',
            data: serialize(form),
            mode: 'replaceWith',
            busy: '#ResourceCreateContainer .fa-spinner',
            alertInto: 'ResourceCreateMessageContainer',
            alertTitle: 'An error occurred attempting to save the new Resource',
            onSuccess: () => {
                if (one('CreateResourceForm')) return;   // still the form: rejected
                toast('success', 'Resource created');
                void reloadList();
            },
        });
        return false;
    },
};

export const Edit = {
    SaveChangesButtonClick(formId: string): void {
        const form = one(formId) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        const save = one('ResourceEditSaveButton') as HTMLButtonElement | null;
        if (save) save.disabled = true;
        void ajaxJson({
            url: 'Resource/Edit',
            method: 'POST',
            data: serialize(form),
            busy: '#ResourceEditSaveBusy',
            alertInto: 'ResourceEditMessageContainer',
            errorTitle: 'Failed to save changes',
            onSuccess: () => {
                hideModal('ResourceModal');
                toast('success', 'Changes saved successfully');
                void reloadList();
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (!modelState) return false;
                setValidationModelState(modelState, 'EditResourceForm');
                return true;
            },
            onComplete: () => {
                if (save) save.disabled = false;
            },
        });
    },

    /**
     * Base URL for bind/unbind, read off the panel the server rendered. The same
     * panel ships on this modal and on the Application detail Certificates tab,
     * whose actions live on different controllers (and carry different
     * authorization), so the route travels with the markup.
     */
    PanelRoute(resourceId: number | string): string {
        return one('ResourceCertificatesPanel')?.getAttribute('data-panel-route')
            || `Resource/Edit/${resourceId}/Certificates`;
    },

    /** Phase 5 mTLS gate: bind a Certificate and swap in the refreshed panel. */
    AddCertificate(resourceId: number | string): void {
        const certId = value('ResourceCertificateAddSelect');
        if (!certId) {
            toast('warning', 'Pick a certificate to bind.');
            return;
        }
        void ajaxSwap({
            container: 'ResourceCertificatesPanel',
            url: `${Edit.PanelRoute(resourceId)}/${certId}`,
            method: 'POST',
            mode: 'replaceWith',
            alertTitle: 'Bind certificate failed',
            onError: (xhr, _status, error) => {
                toast('danger', 'Bind certificate failed', xhr?.responseText || error || 'Bind failed');
                return true;
            },
        });
    },

    RemoveCertificate(resourceId: number | string, certId: number | string): void {
        void ajaxSwap({
            container: 'ResourceCertificatesPanel',
            url: `${Edit.PanelRoute(resourceId)}/${certId}`,
            method: 'DELETE',
            mode: 'replaceWith',
            alertTitle: 'Unbind certificate failed',
            onError: (xhr, _status, error) => {
                toast('danger', 'Unbind certificate failed', xhr?.responseText || error || 'Unbind failed');
                return true;
            },
        });
    },
};

/**
 * Seed the URI preview and the scope baseline whenever a Resource form lands
 * -- first paint, an AJAX swap or a modal. Registered as an enhancer, which is
 * what makes it work for a modal: the old views computed the preview in the
 * ajaxModal onSuccess callback of whichever handler happened to open them, so
 * a form reached any other way showed an empty URI.
 */
export function enhanceResourceForms(root: ParentNode): void {
    const select = root instanceof Element && root.matches('#ResourceDomainId')
        ? root
        : root.querySelector('#ResourceDomainId');
    if (!select) return;
    previousScope = (select as HTMLSelectElement).value;
    Scope.Preview();
}

let previewBound = false;

/**
 * Keep the URI preview live as the operator types.
 *
 * A delegated 'input' listener, not a data-action on the field: core/dispatch
 * fires a data-action on CLICK for a text input, so the handler only ever ran
 * when the field was clicked and never while it was being typed into. The
 * browser pass caught the preview sitting empty through a whole entry.
 * ResourceType is a select, which DOES dispatch on change, but it feeds the
 * same preview, so it is handled here too rather than half in each place.
 */
export function initResourceArea(): void {
    if (previewBound) return;
    previewBound = true;
    const watched = '#ResourceDomainPrefix, #ResourceType';
    document.addEventListener('input', event => {
        if (event.target instanceof Element && event.target.matches(watched)) Scope.Preview();
    });
    document.addEventListener('change', event => {
        if (event.target instanceof Element && event.target.matches(watched)) Scope.Preview();
    });
}
